import {
  Link,
  Navigate,
  Outlet,
  createFileRoute,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { useState } from "react";
import {
  AppShell,
  Badge,
  SidebarBrand,
  SidebarFooter,
  SidebarNavItem,
  SidebarSection,
} from "@jotty/ui";

import { auth } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";

// Pathless layout that gates every child route on a live session AND wraps
// authenticated pages in the AppShell (sidebar + content). Onboarding
// (when no tenants exist yet) bypasses the shell — the user has nowhere to
// navigate to until a workspace is created.
export const Route = createFileRoute("/_authed")({
  component: AuthedLayout,
});

function AuthedLayout() {
  const status = useAuthStore((s) => s.status);
  const tenants = useAuthStore((s) => s.tenants);
  const user = useAuthStore((s) => s.user);
  const path = useLocation({ select: (l) => l.pathname });

  if (status === "unknown" || status === "loading") {
    return <Splash />;
  }
  if (status === "anonymous") {
    return <Navigate to="/auth/sign-in" replace />;
  }
  if (tenants.length === 0 && !path.startsWith("/onboarding")) {
    return <Navigate to="/onboarding/channels" replace />;
  }

  // Onboarding routing. Once finished, keep the user out of the flow; otherwise
  // land them on the step the backend says they're on (survives refresh).
  if (path.startsWith("/onboarding")) {
    if (tenants[0]?.onboarding_completed) {
      return <Navigate to="/" replace />;
    }
    const step = tenants[0]?.onboarding_step ?? 2;
    const stepRoutes: Record<
      number,
      | "/onboarding/channels"
      | "/onboarding/language"
      | "/onboarding/first-meeting"
    > = {
      1: "/onboarding/channels",
      2: "/onboarding/channels",
      3: "/onboarding/language",
      4: "/onboarding/first-meeting",
    };
    const correctRoute = stepRoutes[step] ?? "/onboarding/channels";
    if (path !== correctRoute) {
      return <Navigate to={correctRoute} replace />;
    }
  }

  const activeTenant = tenants[0];

  return (
    <AppShell
      sidebar={
        <>
          <SidebarBrand>
            <Link
              to="/"
              className="flex items-center gap-2 text-lg font-semibold tracking-tight text-[color:var(--color-text)]"
            >
              <span className="grid h-7 w-7 place-items-center rounded-lg bg-[color:var(--color-primary)] text-white">
                <span className="text-sm font-bold">J</span>
              </span>
              Jotty
            </Link>
          </SidebarBrand>

          {activeTenant ? (
            <WorkspaceSwitcher activeTenant={activeTenant} tenants={tenants} />
          ) : null}

          <SidebarSection title="Navigation">
            <NavLink to="/" exact label="Meetings" />
            <NavLink to="/meetings/new" label="New meeting" />
            <NavLink to="/settings/integrations" label="Integrations" />
            <NavLink to="/settings" prefix label="Settings" />
          </SidebarSection>

          <SidebarFooter>
            <UserMenu email={user?.email ?? ""} />
          </SidebarFooter>
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
}

interface NavLinkProps {
  to: string;
  label: string;
  exact?: boolean;
  prefix?: boolean;
}

function WorkspaceSwitcher({
  activeTenant,
  tenants,
}: {
  activeTenant: Tenant;
  tenants: Tenant[];
}) {
  const [open, setOpen] = useState(false);
  const handleSwitch = (tenantId: string) => {
    localStorage.setItem("jotty_active_tenant", tenantId);
    window.location.href = "/";
  };
  return (
    <div className="relative mx-3 mt-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2.5 text-sm shadow-sm transition-colors hover:border-[color:var(--color-border-strong)]"
      >
        <span className="min-w-0 flex-1 text-left">
          <span className="block truncate font-medium text-[color:var(--color-text)]">
            {activeTenant.name}
          </span>
          <span className="block truncate text-xs text-[color:var(--color-text-muted)]">
            {activeTenant.slug}
          </span>
        </span>
        {activeTenant.role ? (
          <Badge tone="primary">{activeTenant.role}</Badge>
        ) : null}
      </button>
      {open ? (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)] py-1 shadow-lg">
          {tenants.map((t) => (
            <button
              key={t.id}
              onClick={() => handleSwitch(t.id)}
              className={`flex w-full px-3 py-2 text-left text-sm transition-colors hover:bg-[color:var(--color-bg)] ${t.id === activeTenant.id ? "font-medium text-[color:var(--color-primary)]" : "text-[color:var(--color-text)]"}`}
            >
              {t.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type Tenant = {
  id: string;
  name: string;
  slug: string;
  role?: string;
  onboarding_step?: number;
};

function NavLink({ to, label, exact, prefix }: NavLinkProps) {
  const path = useLocation({ select: (l) => l.pathname });
  const active = exact
    ? path === to
    : prefix
      ? path.startsWith(to)
      : path === to;
  return (
    <Link to={to}>
      <SidebarNavItem active={active}>{label}</SidebarNavItem>
    </Link>
  );
}

function UserMenu({ email }: { email: string }) {
  const clear = useAuthStore((s) => s.clear);
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const initials =
    email
      .split("@")[0]
      ?.split(".")
      .map((s) => s[0]?.toUpperCase())
      .join("")
      .slice(0, 2) ?? "?";
  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await auth.signOut();
    } finally {
      clear();
      void router.navigate({ to: "/auth/sign-in" });
    }
  };
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-[color:var(--color-bg)]"
      >
        <div className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-[color:var(--color-primary)] text-[10px] font-bold text-white">
          {initials}
        </div>
        <div
          className="min-w-0 truncate text-xs text-[color:var(--color-text-muted)]"
          title={email}
        >
          {email}
        </div>
      </button>
      {open ? (
        <div className="absolute bottom-full left-0 right-0 z-10 mb-1 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)] py-1 shadow-lg">
          <div className="border-b border-[color:var(--color-border)] px-3 py-2">
            <p className="truncate text-xs font-medium text-[color:var(--color-text)]">
              {email}
            </p>
          </div>
          <button
            onClick={() => void handleSignOut()}
            disabled={signingOut}
            className="flex w-full px-3 py-2 text-left text-xs text-[color:var(--color-danger)] hover:bg-[color:var(--color-bg)] disabled:opacity-50"
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Splash() {
  return (
    <div className="flex min-h-screen items-center justify-center text-[color:var(--color-text-muted)]">
      <span className="text-sm font-medium">Loading…</span>
    </div>
  );
}
